/**
 * score.ts v3 rendering helpers shared by DomainDetailPage and
 * PublicReportPage — the score subtitle (denominator honesty) and the
 * sparkline's model-version-change marker. Pulled out of DomainDetailPage so
 * both surfaces use the exact same formula rather than two hand-copies
 * drifting apart. See docs/adr/0001-scoring-applicability.md.
 */
import type { ScoreBreakdown } from '@/components/IssueList';

/**
 * "80/100 · scored on 65 of 83 applicable points · 1 check unverified" — the
 * same formula as api/src/lib/scan/applicability-copy.ts's buildScoreSubtitle
 * (kept in sync by hand across the api/web workspace boundary; both are
 * covered by dedicated tests asserting the exact string). Returns null when
 * there's nothing to explain: a pre-v3 scan (no `score_breakdown`) or a v3
 * scan where nothing was excluded — the caller falls back to "out of 100".
 */
export function scoreSubtitle(score: number | null, breakdown: ScoreBreakdown | null | undefined): string | null {
  if (score === null || !breakdown) return null;
  const { applicableMax, undeterminedMax, components } = breakdown;
  if (applicableMax === undefined || undeterminedMax === undefined) return null;
  if (undeterminedMax === 0 && applicableMax >= 100) return null;
  const denominator = applicableMax + undeterminedMax;
  const unverifiedCount = Object.values(components ?? {}).filter((c) => c?.state === 'undetermined').length;
  let text = `${score}/100 · scored on ${applicableMax} of ${denominator} applicable point${denominator === 1 ? '' : 's'}`;
  if (unverifiedCount > 0) {
    text += ` · ${unverifiedCount} check${unverifiedCount === 1 ? '' : 's'} unverified`;
  }
  return text;
}

/**
 * Index of the first point (in oldest-to-newest order) whose `scoring_version`
 * differs from the immediately preceding point's — the v2->v3 (or any future
 * model-version) boundary. `scoring_version` is null on scans from before
 * versioning existed, so the comparison is gated on both sides being present
 * (a null on either side means "unknown", not "no change" — never draw a
 * marker from an absence). Returns -1 when there's no boundary in the window.
 */
export function findVersionChangeIndex(data: Array<{ scoring_version?: number | null }>): number {
  return data.findIndex(
    (p, i) => i > 0 && p.scoring_version != null && data[i - 1]!.scoring_version != null && p.scoring_version !== data[i - 1]!.scoring_version,
  );
}
